/* eslint-disable react-hooks/exhaustive-deps */
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ContentShell from "components/ContentShell";
import CopyableTag from "components/CopyableTag";
import CustomButton from "components/CustomButton";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { sortArray } from "utils/miscUtils";
import configData from "config/config.json";
import { useSnackbar } from "hooks/useSnackbar";
import { defaultPageNo, defaultPageSize } from "utils/constants";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import sessionHelper from "services/sessionHelper";
import {
  deleteOperatorLogos,
  getOperatorLogos,
  OPERATOR_LOGO_SLOT_META,
} from "services/operatorLogoService";
import { PermittedRoles } from "utils/permissions";

const useStyles = makeStyles((theme: any) => ({
  playerCardParent: {
    padding: "12px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
  },
  idCell: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "40px",
  },
  logoStrip: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
  },
  logoThumbWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
  },
  logoThumb: {
    width: 90,
    height: 44,
    objectFit: "contain",
    // Subtle checker-like neutral background so white logos remain visible.
    background:
      "repeating-conic-gradient(#eee 0% 25%, #fafafa 0% 50%) 50% / 14px 14px",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: 2,
  },
  logoThumbEmpty: {
    width: 90,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px dashed #cfcfcf",
    borderRadius: 4,
    color: "#bdbdbd",
  },
  logoCaption: {
    fontSize: 10,
    color: "#888",
    maxWidth: 90,
    textAlign: "center",
    lineHeight: 1.1,
  },
}));

interface RowActionsProps {
  rowId?: any;
  data: any;
  actionHandlers: Record<string, any>;
}

const RowActions = ({ data, actionHandlers }: RowActionsProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => setAnchorEl(null);
  const handleClick = (event: any) => setAnchorEl(event.currentTarget);

  const handleEdit = () => {
    setAnchorEl(null);
    navigate(
      configData.routes.operatorLogos.edit.replace(":operatorId", data?.operatorId),
    );
  };

  const handleDelete = () => {
    setAnchorEl(null);
    actionHandlers?.onDelete?.(data);
  };

  const hasDeletePermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR_LOGO.DELETE_ROLES,
  );

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Manage Logos
        </MenuItem>
        {hasDeletePermission && (
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete Config
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

/**
 * Renders a logo preview that gracefully degrades to a transparent, dimension-
 * preserving placeholder if the asset fails to load (no broken-image icon),
 * mirroring the game frontend's softlock-safe behaviour.
 */
const LogoThumb = ({ url, label }: { url: string | null; label: string }) => {
  const styles = useStyles();
  const [errored, setErrored] = useState(false);

  return (
    <Box className={styles.logoThumbWrap}>
      {url && !errored ? (
        <img
          src={url}
          alt={label}
          className={styles.logoThumb}
          onError={() => setErrored(true)}
        />
      ) : (
        <Box className={styles.logoThumbEmpty}>
          <BrokenImageOutlinedIcon fontSize="small" />
        </Box>
      )}
      <span className={styles.logoCaption}>{label}</span>
    </Box>
  );
};

const OperatorLogos = () => {
  const styles = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [listData, setListData] = useState<any[]>([]);
  const [filteredList, setFilteredList] = useState<any[]>([]);
  const [orderDirection, setOrderDirection] = useState("asc");

  const searchValue = searchParams.get("search") || "";
  const pageNo = Number(searchParams.get("pageNo")) || defaultPageNo;
  const pageSize = Number(searchParams.get("pageSize")) || defaultPageSize;

  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.OPERATOR_LOGO.UPDATE_ROLES,
  );

  const onChangePage = (_pageNo: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("pageNo", String(_pageNo));
      return p;
    });
  };

  const onPageSizeChange = (event: any) => {
    const newSize = Number(event?.target?.value ?? defaultPageSize);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("pageNo", "1");
      p.set("pageSize", String(newSize));
      return p;
    });
  };

  const onColumnClick = (column: any) => {
    const _list = sortArray([...filteredList], orderDirection, column);
    setFilteredList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const onSearch = (value: any) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (value) {
        p.set("search", value);
      } else {
        p.delete("search");
      }
      p.set("pageNo", String(defaultPageNo));
      return p;
    });
  };

  const buildListData = (items: any[]) => {
    const operatorMap = new Map<string, any>();
    (sessionHelper.allowedOperators || []).forEach((op: any) => {
      operatorMap.set(op._id, op);
    });

    // S3 keys are deterministic, so a replaced logo reuses the same URL. Append a
    // cache-buster on each fetch so returning from the editor shows the latest logo
    // instead of the browser-cached old one.
    const stamp = Date.now();
    const bustCache = (logos: Record<string, string | null>) => {
      const busted: Record<string, string | null> = {};
      Object.keys(logos || {}).forEach((slot) => {
        const url = logos[slot];
        busted[slot] = url ? `${url.split("?")[0]}?t=${stamp}` : url;
      });
      return busted;
    };

    return (items || []).map((item: any) => {
      const id = item.operatorId;
      const op = operatorMap.get(id);
      return {
        operatorId: id,
        operatorName: item.operatorName || op?.operatorName || id,
        platformName: op?.platformName || "-",
        logos: bustCache(item.logos || {}),
      };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await getOperatorLogos();
      const items = resp?.data || [];
      setListData(buildListData(items));
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (data: any) => {
    if (
      !window.confirm(
        `Delete logo configuration for ${data.operatorName || data.operatorId}?`,
      )
    )
      return;
    try {
      await deleteOperatorLogos(data.operatorId);
      showSnackbar("Operator logos deleted successfully", "success");
      fetchData();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    }
  };

  // Apply client-side search filter
  useEffect(() => {
    if (!searchValue) {
      setFilteredList(listData);
    } else {
      const lower = searchValue.toLowerCase();
      setFilteredList(
        listData.filter(
          (item) =>
            item.operatorName?.toLowerCase().includes(lower) ||
            item.operatorId?.toLowerCase().includes(lower),
        ),
      );
    }
  }, [listData, searchValue]);

  useEffect(() => {
    fetchData();
  }, []);

  const columns = [
    {
      id: "operatorId",
      label: "Operator ID",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: any) => (
        <Box className={styles.idCell}>
          <CopyableTag value={data.operatorId} />
        </Box>
      ),
    },
    {
      id: "operatorName",
      label: "Operator",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
      renderCell: (data: any) => (
        <Box sx={{ fontWeight: 700, fontSize: "13px" }}>
          {data.operatorName}
        </Box>
      ),
    },
    {
      id: "logos",
      label: "Logos",
      isSortable: false,
      alignHead: "center",
      alignBody: "center",
      renderCell: (data: any) => (
        <Box className={styles.logoStrip}>
          {OPERATOR_LOGO_SLOT_META.map((slot) => (
            <LogoThumb
              key={slot.key}
              url={data.logos?.[slot.key] || null}
              label={slot.label}
            />
          ))}
        </Box>
      ),
    },
    ...(hasEditPermission
      ? [
          {
            id: "actions",
            label: "Actions",
            alignHead: "center",
            alignBody: "center",
          },
        ]
      : []),
  ];

  return (
    <ContentShell
      title="Operator Logos"
      loading={loading}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <SearchInput
            value={searchValue}
            placeholder="Search operator..."
            onSearch={onSearch}
          />
          {sessionHelper.hasRole(PermittedRoles.OPERATOR_LOGO.CREATE_ROLES) && (
            <CustomButton
              onClick={() => navigate(configData.routes.operatorLogos.create)}
            >
              Create
            </CustomButton>
          )}
        </Box>
      }
    >
      <Box className={styles.playerCardParent}>
        <Table
          columns={columns}
          data={filteredList.slice((pageNo - 1) * pageSize, pageNo * pageSize)}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={filteredList.length}
          highlightAltRow={true}
          disableMaxHeight={true}
          actions={hasEditPermission ? RowActions : undefined}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          actionHandlers={{ onDelete: handleDelete }}
        />
      </Box>
    </ContentShell>
  );
};

export default OperatorLogos;